// Desktop Dev Chat Messages - Scroll area with chat bubbles
// Supports incoming messages, streaming segments, and future chat history loading
// Uses workspace ChatBubble for consistent styling (thought, comment, text, tool_call)

import React, { useEffect, useState, useRef } from 'react';
import ChatBubble from '../../../../workspace/components/ChatBubble';
import { RevoltButton } from '../../../../../components';
import '../../../../workspace/components/ChatBubble.css';
import './DesktopDevChatMessages.css';

const SEGMENT_TYPE_TO_PRESET = {
  thought: 'assistantThinking',
  reasoning: 'assistantThinking',
  comment: 'assistantText',
  tool_call: 'assistantText', // Use text preset until we have full tool_call structure
  function: 'assistantText',
  text: 'assistantText',
  error: 'assistantText',
  system_prompt: 'assistantText',
  generating: 'generating',
};

function getPresetForMessage(m) {
  if (m.role === 'user') return 'userText';
  if (m.segmentType === 'system_prompt') return 'assistantText';
  if (m.segmentType === 'dev_tool_call') return 'assistantFunctionCall';
  const segmentType = (m.segmentType || 'text').toLowerCase();
  return SEGMENT_TYPE_TO_PRESET[segmentType] || 'assistantText';
}

function getDisplayContent(m) {
  if (m.role === 'user') return m.content || '';
  if (m.segmentType === 'system_prompt') return m.content || '';
  if (m.segmentType === 'dev_tool_call') {
    return { arguments: m.paramsText || '' };
  }
  const segmentType = (m.segmentType || 'text').toLowerCase();
  const content = m.content || '';
  if (segmentType === 'text' || segmentType === 'thought' || segmentType === 'reasoning') {
    return content;
  }
  if (segmentType === 'error') {
    return null; // Rendered separately in ErrorBubble
  }
  if (segmentType === 'generating') {
    return ''; // ChatBubble 'generating' preset renders dots animation
  }
  const label = segmentType === 'comment' ? 'Comment' : segmentType === 'tool_call' || segmentType === 'function' ? 'Tool call' : segmentType;
  return content ? `**[${label}]**\n\n${content}` : `**[${label}]**`;
}

function ErrorBubble({ message, stack }) {
  return (
    <div className="desktop-dev-chat-error-bubble">
      <div className="desktop-dev-chat-error-message">{message}</div>
      {stack && (
        <div className="desktop-dev-chat-error-details">
          <div className="desktop-dev-chat-error-details-header">Details</div>
          <pre className="desktop-dev-chat-error-stack">{stack}</pre>
        </div>
      )}
    </div>
  );
}

function NumberedCodeBlock({ code, maxLines = 250 }) {
  const text = typeof code === 'string' ? code : '';
  const linesRaw = text.split('\n');
  const lines = linesRaw.length > maxLines ? linesRaw.slice(0, maxLines) : linesRaw;
  const truncated = linesRaw.length > maxLines;

  return (
    <div className="desktop-dev-chat-tool-approval-code-lines">
      {lines.map((line, i) => (
        <div key={i} className="desktop-dev-chat-tool-approval-code-line">
          <span className="desktop-dev-chat-tool-approval-code-line__num">{i + 1}</span>
          <span className="desktop-dev-chat-tool-approval-code-line__text">
            {line.length > 0 ? line : '\u00A0'}
          </span>
        </div>
      ))}
      {truncated ? (
        <div className="desktop-dev-chat-tool-approval-code-line desktop-dev-chat-tool-approval-code-line--truncated">
          <span className="desktop-dev-chat-tool-approval-code-line__num">{lines.length + 1}</span>
          <span className="desktop-dev-chat-tool-approval-code-line__text">... (truncated)</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {{ id: string, role: 'user'|'assistant', content: string, segmentType?: string }[]} [props.messages]
 */
export function DesktopDevChatMessages({ messages = [], onToolInteractionAction }) {
  const scrollRef = useRef(null);
  const hasNonSystemMessages = messages.some((m) => (m.segmentType || '').toLowerCase() !== 'system_prompt');
  const [pendingDecisionIds, setPendingDecisionIds] = useState(() => new Set());

  useEffect(() => {
    if (!scrollRef.current || messages.length === 0) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    setPendingDecisionIds((prev) => {
      const next = new Set(
        [...prev].filter((id) =>
          messages.some((m) => m.id === id && m.segmentType === 'dev_tool_call' && m.toolUi?.type === 'approval')
        )
      );
      return next.size === prev.size ? prev : next;
    });
  }, [messages]);

  return (
    <div ref={scrollRef} className="desktop-dev-chat-messages-scroll">
      <div className="desktop-dev-chat-messages-inner">
        {messages.length === 0 ? (
          <div className="desktop-dev-chat-messages-empty">
            <span>Messages will appear here</span>
          </div>
        ) : (
          messages.map((m) =>
            m.segmentType === 'error' ? (
              <div key={m.id} className="desktop-dev-chat-message-error">
                <ErrorBubble message={m.content} stack={m.errorStack} />
              </div>
            ) : m.segmentType === 'system_prompt' ? (
              <div
                key={m.id}
                className={[
                  'desktop-dev-chat-system-prompt-row',
                  hasNonSystemMessages ? 'desktop-dev-chat-system-prompt-row--centered' : '',
                ].filter(Boolean).join(' ')}
              >
                <ChatBubble
                  preset="assistantText"
                  content={m.content || ''}
                  role="assistant"
                  showTimestamp={false}
                />
              </div>
            ) : (
              <div key={m.id}>
                {m.segmentType === 'dev_tool_call' && m.toolUi?.type === 'approval' ? (
                  <div className="desktop-dev-chat-tool-approval-bubble">
                    <div className="desktop-dev-chat-tool-approval-header">
                      <div className="desktop-dev-chat-tool-approval-header__left">
                        <span className="desktop-dev-chat-tool-approval-spinner" />
                        <span className="desktop-dev-chat-tool-approval-title">
                          {m.toolUi.title || `${m.functionName || 'tool'} · requires approval`}
                        </span>
                      </div>
                      <div className="desktop-dev-chat-tool-approval-header__right">
                        <span className="desktop-dev-chat-tool-approval-meta">
                          call #{typeof m.toolCallIndex === 'number' ? m.toolCallIndex : 'unknown'}
                        </span>
                      </div>
                    </div>

                    <div className="desktop-dev-chat-tool-approval-body">
                      <div className="desktop-dev-chat-tool-approval-section-title">Proposed function logic</div>
                      <div className="desktop-dev-chat-tool-approval-code">
                        <NumberedCodeBlock code={m.toolUi.codeSnippet || '(no code snippet provided)'} />
                      </div>

                      {m.toolUi.paramsText ? (
                        <>
                          <div className="desktop-dev-chat-tool-approval-section-title">Params (raw)</div>
                          <pre className="desktop-dev-chat-tool-approval-code">
                            <code>{m.toolUi.paramsText}</code>
                          </pre>
                        </>
                      ) : null}

                      <div className="desktop-dev-chat-tool-approval-actions">
                        {(Array.isArray(m.toolUi.actions) ? m.toolUi.actions : []).map((action) => (
                          <RevoltButton
                            key={action.id}
                            type="button"
                            variant={action.variant || 'outline'}
                            disabled={pendingDecisionIds.has(m.id)}
                            onClick={() => {
                              if (pendingDecisionIds.has(m.id)) return;
                              setPendingDecisionIds((prev) => {
                                const next = new Set(prev);
                                next.add(m.id);
                                return next;
                              });
                              onToolInteractionAction?.({
                                runId: m.toolCallRunId,
                                callIndex: m.toolCallIndex,
                                actionId: action.id,
                              });
                            }}
                          >
                            {action.label || action.id}
                          </RevoltButton>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <ChatBubble
                    preset={getPresetForMessage(m)}
                    content={m.role === 'user' ? m.content : getDisplayContent(m)}
                    role={m.role === 'user' ? 'user' : 'assistant'}
                    showTimestamp={false}
                    {...(m.segmentType === 'dev_tool_call'
                      ? {
                          functionName: m.functionName || 'tool',
                          status:
                            m.toolStatus === 'completed' || m.toolStatus === 'failed'
                              ? m.toolStatus
                              : 'running',
                          bubbleDisplayData:
                            m.toolStatus === 'completed' || m.toolStatus === 'failed'
                              ? {
                                  summary:
                                    m.resultSummary ||
                                    (m.toolStatus === 'failed' ? 'Tool failed' : 'Done'),
                                }
                              : undefined,
                          isExpanded: true,
                          isDurationActive:
                            m.toolStatus === 'accumulating' || m.toolStatus === 'executing',
                        }
                      : {})}
                  />
                )}
              </div>
            )
          )
        )}
      </div>
    </div>
  );
}
